"use client";

import {
  BellAlertIcon,
  MagnifyingGlassCircleIcon,
  PlusIcon,
  ShoppingBagIcon,
} from "@heroicons/react/24/outline";
import SearchBar from "@/components/SearchBar";
import Button from "@/components/ui/Button";
import { HOMEPAGE_COPY } from "@/lib/homepage/copy";
import type { SearchResponse } from "@/lib/services/searchService";

type HomepageActionCardProps = {
  onSearch: (query: string) => Promise<SearchResponse>;
  onBrowse: () => void;
  onSell: () => void;
  onNearMe: () => void;
  onRequestItem?: () => void;
  recentItemRequestsEnabled: boolean;
};

export default function HomepageActionCard({
  onSearch,
  onBrowse,
  onSell,
  onNearMe,
  onRequestItem,
  recentItemRequestsEnabled,
}: HomepageActionCardProps) {
  const { browsePrimary, sellSecondary, nearMeSecondary, requestSecondary } =
    HOMEPAGE_COPY.cta;
  const showRequestCta = recentItemRequestsEnabled && Boolean(onRequestItem);

  return (
    <div className="w-full max-w-7xl mb-10 mt-4 md:mt-5">
      <div className="sio-glass rounded-sio-lg p-4 md:p-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-stretch md:gap-4">
          <SearchBar onSearch={onSearch} className="w-full md:flex-1" />
          <Button
            variant="primary"
            className="text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 w-full md:w-auto md:min-w-[12rem] shrink-0"
            onClick={onBrowse}
          >
            <ShoppingBagIcon className="h-5 w-5" />
            {browsePrimary}
          </Button>
        </div>

        <div
          className={`mt-3 grid gap-3 ${
            showRequestCta
              ? "grid-cols-2 sm:grid-cols-3"
              : "grid-cols-1 sm:grid-cols-2"
          }`}
        >
          <Button
            variant="outline"
            className="px-4 py-2 rounded-lg flex items-center justify-center gap-2"
            onClick={onSell}
          >
            <PlusIcon className="h-5 w-5" />
            {sellSecondary}
          </Button>
          {showRequestCta ? (
            <Button
              variant="outline"
              className="px-4 py-2 rounded-lg flex items-center justify-center gap-2"
              onClick={onRequestItem}
            >
              <MagnifyingGlassCircleIcon className="h-5 w-5" />
              {requestSecondary}
            </Button>
          ) : null}
          <Button
            variant="outline"
            className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 ${
              showRequestCta ? "col-span-2 sm:col-span-1" : ""
            }`}
            onClick={onNearMe}
          >
            <BellAlertIcon className="h-5 w-5" />
            {nearMeSecondary}
          </Button>
        </div>
      </div>
    </div>
  );
}
